import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import MainPage from '../components/MainPage';
import { getBlackboardList, getFavorites } from '../actions/api/apis';
import getOperators from '../actions/api/operators/getOperators';
import getCompanies from '../actions/api/company/getCompanies';
import getWorkRecords from '../actions/api/record/getWorkRecords';
import config from '../config';
import { isCompanyAdmin, isGuest, isRootOrSp } from '../lib/tools';
import { ActionTypes as FavoriteType } from '../actions/favorite';
import getTags from '../actions/api/tag/getTags';
import getGroups from '../actions/api/group/getGroups';
import getMultiLevelTags from '../actions/api/tag/getMultiLevelTags';

const INTERVAL_TIMER = 10000;

// ログイン時に保存した検索条件を基にタグ情報を取得する
const loadTagsSearchCriteria = (apiToken, dispatch, params) => {
  const { selectedTagIds, targetGroupId } = params;
  if (selectedTagIds.length > 0 && targetGroupId !== null) {
    dispatch(
      getMultiLevelTags(
        apiToken,
        {
          parentIds: selectedTagIds,
          actionType: 'UPDATE_SELECT_OPTIONS_TAGS',
        },
        targetGroupId,
      ),
    );

    dispatch(
      getTags(apiToken, {
        isEdit: false,
        groupId: targetGroupId,
        parentId: selectedTagIds[selectedTagIds.length - 1],
      }),
    );
  }
};

const updateContainer = (apiToken, dispatch, apiParams) => {
  if (dispatch && apiToken) {
    const {
      innerRole,
      searchOptions = {},
      targetGroupId: groupId = null,
      favoriteNamePartial = null,
      targetOperatorId,
    } = apiParams;
    if (targetOperatorId) {
      // お気に入り一覧取得
      dispatch(
        getFavorites(apiToken, {
          groupId: groupId || null,
          name: favoriteNamePartial || null,
        }),
      );
      const { groupIds: timelineGroupIds, ...timelineSearchOptions } =
        searchOptions;
      const groupIds = (groupId && [groupId]) || timelineGroupIds;
      // 作業レコード一覧取得
      dispatch(
        getWorkRecords(apiToken, {
          ...timelineSearchOptions,
          groupIds,
          operatorId: isGuest(innerRole) ? targetOperatorId : undefined,
        }),
      );
      // タグ一覧取得
      dispatch(getTags(apiToken, { depth: 1, isEdit: true, groupId }));
      if (isRootOrSp(innerRole) || isCompanyAdmin(innerRole)) {
        // Root、SP、企業管理者の場合は会社情報を取得
        dispatch(getCompanies(apiToken));
      }
    }
  }
};
const scrollTop = () => {
  // ページネーションなどでページ更新した時にスクロールバーをトップに戻す
  const obj = document.getElementById('canScrollTable');
  if (obj) {
    obj.scrollTop = 0;
  }
};

const blackboardScrollTop = () => {
  const obj = document.getElementById('canScrollTableBlackboard');
  if (obj) {
    obj.scrollTop = 0;
  }
};

class MainContainer extends Component {
  constructor(props) {
    super(props);
    const {
      refreshBlackboard,
      refreshFavorite,
      apiToken,
      innerRole,
      selectedTagIds,
      canLoadSearchCriteria,
      dispatch,
      showUserPrompt,
      targetGroupId,
      stopApiCallTimer,
      favoriteNamePartial,
      operatorSearchOptions,
      searchOptions,
    } = props;
    this.state = { refreshBlackboard, refreshFavorite };
    const params = {
      apiToken,
      innerRole,
      targetOperatorId: (props && props.targetOperatorId) || {},
      searchOptions: searchOptions || {},
      selectedTagIds,
      canLoadSearchCriteria,
      dispatch,
      showUserPrompt,
      targetGroupId,
      canUpdate: (props && !props.showUserPrompt) || (props && props.isLogin),
      stopApiCallTimer,
      favoriteNamePartial,
    };

    dispatch({ type: 'CLOSE_LOADING' });
    dispatch({ type: 'CLOSE_MENU' });
    dispatch({ type: 'RESET_OPERATOR_INFO' });
    dispatch({ type: 'CLOSE_FULLSCREEN' });

    // 保存したタグ検索条件を取得する前に画面遷移することを防ぐため、targetGroupIdが設定されるまでローディング画面を表示する。
    if (
      targetGroupId === null ||
      targetGroupId === undefined ||
      targetGroupId === ''
    ) {
      dispatch({ type: 'OPEN_LOADING' });
    }

    // 作業者一覧取得(地図と作業者一覧共用)
    // デフォルトグループに所属する作業者を、作業者一覧で表示するかどうかバックエンドで判断するため、グループIDは必須で指定する
    if (targetGroupId !== null || targetGroupId !== undefined) {
      dispatch(
        getOperators(apiToken, {
          accountStatuses: config.searchAccountStatus.enable,
          ...operatorSearchOptions,
          groupIds: [targetGroupId],
        }),
      );
    }
    dispatch(getGroups(apiToken, {}, 'SUCCESS_API_GET_USER_GROUPS'));
    if (params.canUpdate) {
      updateContainer(apiToken, dispatch, {
        innerRole,
        targetGroupId,
        favoriteNamePartial,
      });
      this.initialTimer(params);
    } else {
      this.closeTimer();
    }
  }

  shouldComponentUpdate(props, state) {
    scrollTop();
    const {
      dispatch,
      apiToken,
      isLogin,
      innerRole,
      targetOperatorId,
      searchOptions,
      showUserPrompt,
      targetGroupId,
      stopApiCallTimer,
      refreshBlackboard,
      refreshFavorite,
      favorite,
      favoriteNamePartial,
      blackboardSearchOptions,
    } = props;
    const params = {
      apiToken,
      innerRole,
      targetOperatorId,
      searchOptions,
      dispatch,
      showUserPrompt,
      targetGroupId,
      canUpdate: !showUserPrompt || isLogin,
      stopApiCallTimer,
      favoriteNamePartial,
    };
    if (params.canUpdate) {
      this.initialTimer(params);
    } else {
      this.closeTimer();
    }
    if (refreshBlackboard !== state.refreshBlackboard) {
      dispatch(getBlackboardList(apiToken, blackboardSearchOptions));
      this.setState({ refreshBlackboard });
      blackboardScrollTop();
      return true;
    }
    if (refreshFavorite !== state.refreshFavorite) {
      updateContainer(apiToken, dispatch, {
        innerRole,
        targetGroupId,
        favoriteNamePartial,
      });
      this.initialTimer(params);
      return true;
    }
    if (favorite) {
      const accountStatuses = config.searchAccountStatus.enable;
      const groupId = favorite.id ? favorite.groupId : targetGroupId;
      const options = {
        accountStatuses,
        groupIds: groupId ? [groupId] : null,
      };
      dispatch(
        getOperators(apiToken, options, FavoriteType.SUCCESS_API_GET_OPERATORS),
      );
      return true;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const {
      targetGroupId,
      dispatch,
      apiToken,
      operatorSearchOptions,
      selectedTagIds,
      canLoadSearchCriteria,
      innerRole,
    } = this.props;

    dispatch({ type: 'CLOSE_LOADING' });
    if (prevProps.targetGroupId !== targetGroupId) {
      if (targetGroupId !== null || targetGroupId !== undefined) {
        dispatch(
          getOperators(apiToken, {
            accountStatuses: config.searchAccountStatus.enable,
            ...operatorSearchOptions,
            groupIds: [targetGroupId],
          }),
        );
      }
    }

    // 保存済みの検索条件をもとにタグ情報を取得
    if (
      !isGuest(innerRole) &&
      targetGroupId &&
      selectedTagIds.length > 0 &&
      canLoadSearchCriteria
    ) {
      loadTagsSearchCriteria(apiToken, dispatch, {
        selectedTagIds,
        targetGroupId,
      });
      dispatch({ type: 'FINISH_LOAD_SEARCH_CRITERIA' });
    }

    // 連絡先の作業者一覧取得
    if (prevProps.operatorSearchOptions !== operatorSearchOptions) {
      dispatch(
        getOperators(apiToken, {
          accountStatuses: config.searchAccountStatus.enable,
          ...operatorSearchOptions,
          groupIds: [targetGroupId],
        }),
      );
    }
  }

  componentWillUnmount() {
    this.closeTimer();
  }

  initialTimer(params) {
    const {
      userInfo,
      innerRole,
      targetOperatorId,
      dispatch,
      apiToken,
      searchOptions,
      showUserPrompt,
      targetGroupId,
      stopApiCallTimer,
      favoriteNamePartial,
    } = params;
    this.closeTimer();
    const apiParams = {
      userInfo,
      innerRole,
      searchOptions,
      targetGroupId,
      favoriteNamePartial,
      targetOperatorId,
    };
    if (dispatch && apiToken && !showUserPrompt) {
      if (!stopApiCallTimer) {
        updateContainer(apiToken, dispatch, apiParams);
      }
      this.mainTimer = setInterval(() => {
        if (!showUserPrompt && !stopApiCallTimer) {
          updateContainer(apiToken, dispatch, apiParams);
        }
      }, INTERVAL_TIMER);
    }
  }

  closeTimer() {
    if (this.mainTimer !== undefined) {
      clearInterval(this.mainTimer);
      delete this.mainTimer;
    }
  }

  render() {
    const { isLogin } = this.props;
    return (!isLogin && <Redirect to="/login" />) || <MainPage />;
  }
}

MainContainer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  innerRole: PropTypes.number.isRequired,
  targetOperatorId: PropTypes.number.isRequired,
  isLogin: PropTypes.bool.isRequired,
  apiToken: PropTypes.string.isRequired,
  searchOptions: PropTypes.object.isRequired,
  selectedTagIds: PropTypes.array.isRequired,
  canLoadSearchCriteria: PropTypes.bool.isRequired,
  operatorSearchOptions: PropTypes.object.isRequired,
  showUserPrompt: PropTypes.bool.isRequired,
  targetGroupId: PropTypes.string.isRequired,
  stopApiCallTimer: PropTypes.bool.isRequired,
  refreshBlackboard: PropTypes.bool.isRequired,
  blackboardSearchOptions: PropTypes.object.isRequired,
  refreshFavorite: PropTypes.bool.isRequired,
  favorite: PropTypes.object.isRequired,
  favoriteNamePartial: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  innerRole: state.main.innerRole,
  targetOperatorId: state.main.userInfo.operatorId,
  isLogin: state.main.isLogin,
  apiToken: state.main.apiToken,
  searchOptions: state.timeline.searchOptions,
  selectedTagIds: state.timeline.selectedTagIds,
  canLoadSearchCriteria: state.timeline.canLoadSearchCriteria,
  operatorSearchOptions: state.operator.searchOptions,
  showUserPrompt: state.userPrompt.showUserPrompt,
  targetGroupId: state.main.targetGroupId,
  stopApiCallTimer: state.main.stopApiCallTimer,
  refreshBlackboard: state.blackboard.refresh,
  blackboardSearchOptions: state.blackboard.searchOptions,
  refreshFavorite: state.favorite.refreshPage,
  favorite: state.favorite.favorite,
  favoriteNamePartial: state.favorite.namePartial,
});

export default connect(mapStateToProps)(MainContainer);
